import { useCallback, useSyncExternalStore } from "react";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { createMealPlan } from "../services/mealPlansService";
import {
  addPlan,
  deletePlan,
  getCreatedPlans,
  getDeletedPlanIds,
  getPlans,
  getSessionPlans,
  subscribeToPlans,
} from "../services/plansService";
import type { NewPlan, PlanRecord } from "../types/plans.types";

export interface PlansState {
  /** Every plan the browser holds, newest first. */
  plans: PlanRecord[];
  /**
   * Creates one on the backend and hands back what it made, so the caller can
   * open it. Rejects if the request does, leaving the dialog to report it —
   * nothing is stored locally unless the server said yes.
   */
  create: (input: NewPlan) => Promise<PlanRecord>;
  remove: (planId: string) => void;
}

/**
 * The plan list, kept in step with `plansService`.
 *
 * No loading or failed state, because nothing is fetched: the store is in the
 * browser. Both arrive here the day the endpoint does, and the screens gain a
 * `status` the way `useClients` has one — until then a state neither screen
 * can enter would only be a lie for them to render.
 */
export function usePlans(): PlansState {
  const getToken = useTokenGetter();
  const plans = useSyncExternalStore(subscribeToPlans, getPlans, getPlans);

  const create = useCallback(
    async (input: NewPlan) => {
      const record = await createMealPlan(getToken, {
        client: input.clientId,
        // The id, not the name — see `CreateMealPlanPayload`.
        plan_type: input.planTypeId,
        name: input.name,
      });

      // Only once the server has answered: a plan that failed to be created
      // must not be sitting at the top of the list as though it had been.
      //
      // The server's own client name wins, since it is the one the rest of
      // the list is labelled with — but it falls back to what the dialog
      // collected rather than leaving the row's client column blank.
      return addPlan({
        ...record,
        clientName: record.clientName || input.clientName,
      });
    },
    [getToken]
  );

  return { plans, create, remove: deletePlan };
}

/**
 * The plans this sitting has made or changed.
 *
 * The builder edits the browser's copy — there is no endpoint to save a plan's
 * meals to — so the list screens show these in place of the row they fetched,
 * which is what keeps a plan just activated from reading as a draft again.
 */
export function useSessionPlans(): PlanRecord[] {
  return useSyncExternalStore(
    subscribeToPlans,
    getSessionPlans,
    getSessionPlans
  );
}

/**
 * Only the plans made in this sitting — the ones no page of the server's list
 * holds, since it was asked before they existed.
 *
 * Kept apart from the session plans above because the lists are paginated: a
 * plan merely edited is somewhere in the server's own pages and is replaced
 * there, and putting it on top as well would show it twice.
 */
export function useCreatedPlans(): PlanRecord[] {
  return useSyncExternalStore(
    subscribeToPlans,
    getCreatedPlans,
    getCreatedPlans
  );
}

/**
 * The plans deleted in this sitting. The server has not been told — there is
 * no endpoint for that either — so it keeps returning them, and the lists
 * drop them from what they fetched rather than showing a row the user was
 * just told had gone.
 */
export function useDeletedPlanIds(): string[] {
  return useSyncExternalStore(
    subscribeToPlans,
    getDeletedPlanIds,
    getDeletedPlanIds
  );
}
